/**
 * A cms draft is previewed by putting ?draftId=… on a normal page url, which makes useCms
 * fetch `{cmsContent}drafts/{draftId}/{resourceUri}` instead of the published file.
 *
 * The id is pasted straight into that url, so anything able to climb out of `drafts/` would
 * turn a preview link into a way to read arbitrary paths on the content origin.
 */
export function isSafeDraftId(draftId: string): boolean {
	if (!draftId) return false;
	if (draftId.includes('/') || draftId.includes('\\')) return false;
	if (draftId.includes('..')) return false;
	// deliberately not a charset allowlist: the ids come from the cms and guessing at their
	// format risks breaking previews that work today
	return draftId === encodeURIComponent(draftId);
}

/** True when this request is rendering a draft preview rather than the published page. */
export function hasDraftParam(url: string): boolean {
	const queryStart = url.indexOf('?');
	if (queryStart === -1) return false;

	const draftId = new URLSearchParams(url.slice(queryStart)).get('draftId');
	return draftId !== null && isSafeDraftId(draftId);
}

/**
 * The file a draft preview is of, or null when this is not a preview.
 *
 * A page asks for a category's or brand's cms file only when the cms index says it exists,
 * and the index is rebuilt on publish - so a draft of a file that has never been published is
 * in nobody's index. The preview link names the file, and that is reason enough to ask.
 */
export function previewedFile(search: string): string | null {
	const params = new URLSearchParams(search.startsWith('?') ? search : search.slice(search.indexOf('?')));
	const draftId = params.get('draftId');
	if (draftId === null || !isSafeDraftId(draftId)) return null;
	return params.get('draftFile');
}
